/**
 * La caja, la tira, el saco: vender el mismo producto de otra forma.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE PIDIÓ EL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   «para un supermercado que también venda las tiras, no deberíamos agregar
 *    un botón para que se pueda vender en caja, bulto, etc. [...] que no sea
 *    tan incómodo para la lista»
 *
 * En el servidor, «Coca-Cola (Caja ×24)» es un combo de una sola pieza: 24
 * Coca-Colas. Llega en el catálogo de Vender como una fila más, con
 * `presentacion_de_id` apuntando a su producto.
 *
 * ⚠️ Y NO SE PINTA COMO UNA TARJETA MÁS. Sería el mismo refresco tres veces en
 * la grilla. Vive DENTRO de su producto: al tocarlo se pregunta «¿unidad o
 * caja?», y el lector, al escanear el código de la caja, la mete directo.
 *
 * Va aparte de `PosPage.tsx` para poder probarlo.
 */

import type { ItemListRow } from "@/lib/types";

/** Si esta fila es la caja o la tira de otro producto. */
export function esPresentacion(item: Pick<ItemListRow, "presentacion_de_id">): boolean {
  return Boolean(item.presentacion_de_id);
}

/**
 * `{producto: [sus cajas]}`, en el orden en que el dueño las puso.
 *
 * Solo entran las cajas cuyo producto está en la lista: la de un producto que
 * no se vende no tiene dónde colgarse.
 */
export function presentacionesPorProducto(
  items: readonly ItemListRow[],
): Map<string, ItemListRow[]> {
  const ids = new Set(items.map((i) => i.id));
  const mapa = new Map<string, ItemListRow[]>();
  for (const i of items) {
    const padre = i.presentacion_de_id;
    if (!padre || !ids.has(padre)) continue;
    const lista = mapa.get(padre) ?? [];
    lista.push(i);
    mapa.set(padre, lista);
  }
  return mapa;
}

/** Cuántas unidades del producto trae: 24 en la caja de 24. */
export function unidadesQueTrae(hija: Pick<ItemListRow, "presentacion_unidades">): number {
  const n = Number(hija.presentacion_unidades ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Cuántas cajas se pueden vender con lo que hay: con 30 latas, 1 caja de 24.
 *
 * `null` si el producto no lleva la cuenta de su inventario: ahí no hay número
 * que enseñar, igual que en la esquina de la tarjeta.
 */
export function cuantasAlcanzan(
  padre: Pick<ItemListRow, "existencia">,
  hija: Pick<ItemListRow, "presentacion_unidades">,
): number | null {
  if (padre.existencia == null) return null;
  const unidades = unidadesQueTrae(hija);
  if (unidades <= 0) return null;
  // La millonésima evita que 0,3 kg ÷ 0,1 (que la computadora da como
  // 2,9999999…) diga que alcanzan 2 en vez de 3. Ver `redondearMoneda`.
  return Math.max(0, Math.floor(Number(padre.existencia) / unidades + 1e-6));
}

/** A cuánto sale cada unidad dentro de la caja: $20 la caja de 24 → $0,83. */
export function precioPorUnidad(hija: Pick<ItemListRow, "price_with_tax" | "presentacion_unidades">): number {
  const unidades = unidadesQueTrae(hija);
  return unidades > 0 ? Number(hija.price_with_tax) / unidades : 0;
}

/**
 * Cuánto se ahorra llevándose la caja entera, de 0 a 1. `null` si no sale más
 * barata — ahí no hay nada que anunciar, y un «ahorras 0 %» invita a pensar
 * que el precio está mal puesto.
 */
export function cuantoSeAhorra(
  padre: Pick<ItemListRow, "price_with_tax">,
  hija: Pick<ItemListRow, "price_with_tax" | "presentacion_unidades">,
): number | null {
  const suelto = Number(padre.price_with_tax);
  if (!(suelto > 0)) return null;
  const ahorro = 1 - precioPorUnidad(hija) / suelto;
  return ahorro > 0.005 ? ahorro : null;
}

/** A qué producto va una caja y cuántas unidades le cuenta. */
export type DeQuienEs = Map<string, { padreId: string; unidades: number; unidad: string | null; permiteDecimales: boolean }>;

export function deQuienEsCadaCaja(items: readonly ItemListRow[]): DeQuienEs {
  const porId = new Map(items.map((i) => [i.id, i]));
  const mapa: DeQuienEs = new Map();
  for (const i of items) {
    const padre = i.presentacion_de_id ? porId.get(i.presentacion_de_id) : undefined;
    if (!padre) continue;
    mapa.set(i.id, {
      padreId: padre.id,
      unidades: unidadesQueTrae(i),
      unidad: padre.unit,
      permiteDecimales: Boolean(padre.allow_decimal_qty),
    });
  }
  return mapa;
}

/**
 * Las líneas de la cuenta contadas en UNIDADES DEL PRODUCTO.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ UNA CAJA SON 24 COCA-COLAS, NO «UNA COSA MÁS»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Dos sitios lo necesitan, y los dos se equivocarían sin esto:
 *
 *   · La tarjeta de la Coca-Cola: con una caja en la cuenta tiene que salir en
 *     verde, arriba, y comparar 24 contra lo que queda — no 1.
 *   · La esquina del stock al cobrar: vendida la caja, bajan 24. Restándole 1
 *     a la caja (que no tiene stock propio) no bajaría nada.
 */
export function enUnidadesDelProducto<
  L extends { itemId: string; cantidad: number; unidad: string | null; permiteDecimales: boolean },
>(lineas: readonly L[], deQuienEs: DeQuienEs): L[] {
  if (deQuienEs.size === 0) return lineas as L[];
  return lineas.map((l) => {
    const suyo = deQuienEs.get(l.itemId);
    if (!suyo) return l;
    return {
      ...l,
      itemId: suyo.padreId,
      cantidad: l.cantidad * suyo.unidades,
      unidad: suyo.unidad,
      permiteDecimales: suyo.permiteDecimales,
    };
  });
}
